/**
 * useAudioDevices Hook
 *
 * Enumerates audio input devices, separately detects true loopback devices
 * (Stereo Mix, What U Hear) vs virtual cables (VB-Cable) that redirect audio.
 * Re-scans on device change events.
 *
 * IMPORTANT: Virtual cables (VB-Cable, Cable Output) are NOT loopback devices.
 * - Loopback: mirrors speaker output passively — audio still reaches headset
 * - Virtual cable: redirects audio through a pipe — breaks live call audio
 */

import { useState, useEffect, useCallback } from 'react'

export interface AudioDevice {
  deviceId: string
  label: string
  isLoopback: boolean
  isVirtualCable: boolean
}

interface UseAudioDevicesReturn {
  devices: AudioDevice[]
  loopbackDevice: AudioDevice | null
  virtualCableDevice: AudioDevice | null
  hasLoopback: boolean
  hasVirtualCable: boolean
  isScanning: boolean
  rescan: () => Promise<void>
}

// True loopback devices — mirror speaker output without redirecting it
const LOOPBACK_PATTERNS = [
  /stereo mix/i,
  /what u hear/i,
  /wave out/i,
  /mix\s*\(/i,
  /loopback/i,
]

// Virtual cables — redirect audio through a pipe (breaks live calls when used alone)
const VIRTUAL_CABLE_PATTERNS = [
  /vb-cable/i,
  /cable output/i,
  /virtual audio cable/i,
]

// Voicemeeter is a mixer that CAN work, but requires proper routing setup
const MIXER_PATTERNS = [
  /voicemeeter/i,
  /virtual audio/i,
]

function isLoopbackDevice(label: string): boolean {
  return LOOPBACK_PATTERNS.some(pattern => pattern.test(label))
}

function isVirtualCableDevice(label: string): boolean {
  return VIRTUAL_CABLE_PATTERNS.some(pattern => pattern.test(label))
}

function isMixerDevice(label: string): boolean {
  return MIXER_PATTERNS.some(pattern => pattern.test(label))
}

export function useAudioDevices(): UseAudioDevicesReturn {
  const [devices, setDevices] = useState<AudioDevice[]>([])
  const [isScanning, setIsScanning] = useState(false)

  const scan = useCallback(async () => {
    setIsScanning(true)
    try {
      // Need permission first to get device labels
      // Try enumerate without permission first (labels may be empty)
      let rawDevices = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = rawDevices.filter(d => d.kind === 'audioinput')

      // If labels are empty, we need mic permission to see them
      if (audioInputs.length > 0 && !audioInputs[0].label) {
        try {
          const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true })
          tempStream.getTracks().forEach(t => t.stop())
          // Wait for the virtual device (e.g. VB-Cable) to fully release the hardware
          // before re-enumerating. Without this pause the device may not yet appear
          // correctly, and a subsequent getUserMedia call can capture a silent track.
          await new Promise<void>(resolve => setTimeout(resolve, 200))
          rawDevices = await navigator.mediaDevices.enumerateDevices()
        } catch {
          // Permission denied — can't get labels, proceed without loopback detection
        }
      }

      const mapped: AudioDevice[] = rawDevices
        .filter(d => d.kind === 'audioinput')
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone (${d.deviceId.slice(0, 8)})`,
          isLoopback: isLoopbackDevice(d.label),
          isVirtualCable: isVirtualCableDevice(d.label) || isMixerDevice(d.label),
        }))

      setDevices(mapped)
    } catch (err) {
      console.warn('[AudioDevices] Scan failed:', err)
    } finally {
      setIsScanning(false)
    }
  }, [])

  // Initial scan
  useEffect(() => {
    scan()
  }, [scan])

  // Re-scan on device change (USB headset plugged in, etc.)
  useEffect(() => {
    const handler = () => { scan() }
    navigator.mediaDevices?.addEventListener('devicechange', handler)
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handler)
    }
  }, [scan])

  const loopbackDevice = devices.find(d => d.isLoopback) || null
  const virtualCableDevice = devices.find(d => d.isVirtualCable) || null

  return {
    devices,
    loopbackDevice,
    virtualCableDevice,
    hasLoopback: !!loopbackDevice,
    hasVirtualCable: !!virtualCableDevice,
    isScanning,
    rescan: scan,
  }
}
